/**
 * Utility functions for parsing and constructing ATI strings
 * Uses @atlassian/ari package for ATI parsing
 */

import { Ati } from '@atlassian/ari';

import type { ParsedATI } from './types';

/**
 * Input for constructATI function
 */
export interface ConstructATIInput {
	/** Resource owner (e.g., "jira", "confluence") */
	owner: string;
	/** ATI prefix (e.g., "cloud", "third-party") */
	prefix: string;
	/** Resource type (e.g., "issue", "page") */
	type: string;
}

/**
 * Parse an ATI string into its component parts
 *
 * @param ati - ATI string in format "ati:<prefix>:<owner>:<type>"
 * @returns Parsed ATI components
 * @throws Error if ATI format is invalid
 *
 * @example
 * parseATI("ati:cloud:jira:issue")
 */
export function parseATI(ati: string): ParsedATI {
	try {
		const parsed = Ati.parse(ati);
		return {
			prefix: parsed.platformQualifier,
			owner: parsed.resourceOwner,
			type: parsed.resourceType,
		};
	} catch {
		throw new Error(`Invalid ATI format: expected "ati:<prefix>:<owner>:<type>", got "${ati}"`);
	}
}

/**
 * Construct an ATI string from its component parts
 *
 * @param input - Input parameters
 * @param input.prefix - ATI prefix (e.g., "cloud", "third-party")
 * @param input.owner - Resource owner (e.g., "jira", "confluence")
 * @param input.type - Resource type (e.g., "issue", "page")
 * @returns Constructed ATI string
 *
 * @example
 * constructATI({ prefix: "cloud", owner: "jira", type: "issue" })
 */
export function constructATI({ prefix, owner, type }: ConstructATIInput): string {
	if (!prefix || !owner || !type) {
		throw new Error('All ATI components (prefix, owner, type) must be non-empty');
	}

	try {
		const ati = Ati.create({
			platformQualifier: prefix as 'cloud' | 'third-party',
			resourceOwner: owner,
			resourceType: type,
		});
		return ati.toString();
	} catch {
		throw new Error('Failed to construct ATI from components');
	}
}

/**
 * Validate that a string is a well-formed ATI
 *
 * @param ati - String to validate
 * @returns true if valid ATI format, false otherwise
 */
export function isValidATI(ati: unknown): ati is Ati {
	if (typeof ati !== 'string') {
		return false;
	}

	try {
		Ati.parse(ati);
		return true;
	} catch {
		return false;
	}
}

/**
 * Creates a mapping from strings to sequential numeric indices.
 * Used for binary encoding/decoding of ATI components.
 *
 * @param items - Array of strings to map to indices
 * @returns Record mapping each string to its index (starting from 0)
 */
export function createIndexMap(items: readonly string[]): Record<string, number> {
	return Object.fromEntries(items.map((item, index) => [item, index]));
}
